import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  updateDashGraphSize,
  updateImageElement,
  updateRichTextElement,
} from '../../redux/TabTile/TabActions';

type ItemRect = {
  id: string;
  type: 'tile' | 'image' | 'richText';
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Custom hook that handles adjusting dashboard item positions for gap enforcement.
 * Runs on any theme selection — if items touch dashboard edges or other items,
 * they are shrunk by 1 grid unit so shadows/borders are properly visible.
 */
const useThemeAdjustment = () => {
  const dispatch = useDispatch();

  const adjustAllItemsForTheme = useCallback(
    (tabState: any, selectedTab: number) => {
      const currentTab = tabState.tabs[selectedTab];
      if (!currentTab) return;

      const tilesInDash = currentTab.tilesInDashboard || [];
      const dashDetails = currentTab.dashTilesDetails || {};
      const imageElements = currentTab.dashboardObjects?.imageElements || [];
      const richTextElements = currentTab.dashboardObjects?.richTextElements || [];

      const resolutionType = currentTab.resolutionSettings?.resolutionType;
      const dashboardRows = currentTab.resolutionSettings?.dashboardRows || 32;

      // Collect all items into one list for adjacency checking
      const allItems: ItemRect[] = [];

      tilesInDash.forEach((propKey: string) => {
        const d = dashDetails[propKey];
        if (!d) return;
        allItems.push({
          id: propKey,
          type: 'tile',
          x: d.x,
          y: d.y,
          width: d.width,
          height: d.height,
        });
      });
      imageElements.forEach((el: any) => {
        allItems.push({
          id: el.id,
          type: 'image',
          x: el.x,
          y: el.y,
          width: el.width,
          height: el.height,
        });
      });
      richTextElements.forEach((el: any) => {
        allItems.push({
          id: el.id,
          type: 'richText',
          x: el.x,
          y: el.y,
          width: el.width,
          height: el.height,
        });
      });

      // For each item, detect which sides are touching the dashboard edge or another item.
      // Only the right/bottom item of a touching pair adjusts, so we get exactly 1 gap, not 2.
      allItems.forEach((item) => {
        let { x, y, width, height } = item;

        let itemX = Math.round(x);
        let itemY = Math.round(y);
        let itemW = Math.round(width);
        let itemH = Math.round(height);

        let touchesTop = itemY === 0; // dashboard top edge
        let touchesLeft = itemX === 0; // dashboard left edge

        const GRID_WIDTH = 64;
        let touchesRight = itemX + itemW >= GRID_WIDTH; // dashboard right edge

        let touchesBottom = false; // dashboard bottom edge
        if (resolutionType === 'autoWidth') {
          touchesBottom = itemY + itemH >= dashboardRows;
        } else {
          touchesBottom = itemY + itemH >= 32;
        }

        for (const other of allItems) {
          if (other.id === item.id && other.type === item.type) continue;

          let otherX = Math.round(other.x);
          let otherY = Math.round(other.y);
          let otherW = Math.round(other.width);
          let otherH = Math.round(other.height);

          const isOverlappingX = itemX < otherX + otherW && itemX + itemW > otherX;
          const isOverlappingY = itemY < otherY + otherH && itemY + itemH > otherY;

          // Another item's bottom edge touches this item's top — this item is below, so it adjusts
          if (!touchesTop && otherY + otherH === itemY && isOverlappingX) touchesTop = true;
          // Another item's right edge touches this item's left — this item is to the right, so it adjusts
          if (!touchesLeft && otherX + otherW === itemX && isOverlappingY) touchesLeft = true;

          // NOTE: We do not set touchesRight or touchesBottom for inter-item collisions here.
          // This guarantees exactly a 1-unit gap between items, as only the bottom/right item adjusts.

          if (touchesTop && touchesLeft && touchesRight && touchesBottom) break;
        }

        // Only adjust if item is actually touching something on any edge
        if (!touchesTop && !touchesLeft && !touchesRight && !touchesBottom) return;

        // Shrink by 1 grid unit from the touching sides.
        // Also ensure we write back clean integers, not the floats we started with.
        let newX = itemX;
        let newY = itemY;
        let newW = itemW;
        let newH = itemH;

        if (touchesTop) {
          newY += 1;
          newH -= 1;
        }
        if (touchesLeft) {
          newX += 1;
          newW -= 1;
        }
        if (touchesRight) {
          newW -= 1;
        }
        if (touchesBottom) {
          newH -= 1;
        }

        newW = Math.max(1, newW);
        newH = Math.max(1, newH);

        x = newX;
        y = newY;
        width = newW;
        height = newH;

        if (item.type === 'tile') {
          dispatch(updateDashGraphSize(selectedTab, item.id, x, y, width, height));
        } else if (item.type === 'image') {
          dispatch(updateImageElement(selectedTab, item.id, { x, y, width, height }));
        } else if (item.type === 'richText') {
          dispatch(updateRichTextElement(selectedTab, item.id, { x, y, width, height }));
        }
      });
    },
    [dispatch],
  );

  return { adjustAllItemsForTheme };
};

export default useThemeAdjustment;
